import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { PaginationControls } from '../../components/PaginationControls';

const IFStudentAdmissionList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [students, setStudents] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0
  });

  const [filters, setFilters] = useState({
    first_name: '',
    last_name: '',
    email: ''
  });

  const LMS = process.env.REACT_APP_API_LMS;
  const token = process.env.REACT_APP_API_TOKEN;

  const fetchStudents = async (page = 1) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: page.toString()
      });

      if (filters.first_name.trim()) queryParams.append('search', filters.first_name.trim());
      if (filters.last_name.trim()) queryParams.append('search', filters.last_name.trim());
      if (filters.email.trim()) queryParams.append('search', filters.email.trim());

      const response = await fetch(`${LMS}/api/student-admissions?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Accept': 'application/json'
        }
      });
      const result = await response.json();
      if (result.status) {
        setStudents(result.data.data);
        setPagination({
          currentPage: result.data.current_page,
          lastPage: result.data.last_page,
          total: result.data.total
        });
      }
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${LMS}/api/student-admissions/export`, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `student_admissions_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Export failed:', errorData.message || 'Unknown error');
      }
    } catch (error) {
      console.error('Error during export:', error);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchStudents(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters]);

  const handlePageChange = (page) => {
    fetchStudents(page);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      first_name: '',
      last_name: '',
      email: ''
    });
  };

  const handleViewDetails = (id) => {
    navigate(`/teacher-student-admission-detail/${id}`);
  };

  const columns = [
    // { key: 'student_id', label: 'Student ID' },
    { key: 'name', label: 'Name' },
    // { key: 'email_id', label: 'Email' },
    { key: 'mobile_no', label: 'Mobile' },
    { key: 'education_level', label: 'Education' },
    { key: 'created_at', label: 'Submitted On' },
    { key: 'actions', label: 'Actions' }
  ];

  return (
    <div>
      <div className="row">
        <div className="col-12">
          <PageHeader
            title="Student Admission Forms"
            breadcrumbs={[
              { label: "Dashboard", path: "/teacher-dashboard" },
              { label: "Admission Forms", active: true }
            ]}
          />
        </div>
      </div>

      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body p-3">
          <div className="row g-2">
            <div className="col-md-3">
              <label className="form-label fw-semibold">First Name</label>
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <i className="bi bi-person text-muted"></i>
                </span>
                <input
                  type="text"
                  name="first_name"
                  className="form-control border-start-0 ps-0"
                  placeholder="Filter by first name..."
                  value={filters.first_name}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">Last Name</label>
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <i className="bi bi-person text-muted"></i>
                </span>
                <input
                  type="text"
                  name="last_name"
                  className="form-control border-start-0 ps-0"
                  placeholder="Filter by last name..."
                  value={filters.last_name}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">Email</label>
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <i className="bi bi-envelope text-muted"></i>
                </span>
                <input
                  type="text"
                  name="email"
                  className="form-control border-start-0 ps-0"
                  placeholder="Filter by email address..."
                  value={filters.email}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
            <div className="col-md-3 d-flex align-items-end gap-2">
              <button
                className="btn btn-outline-secondary flex-grow-1"
                onClick={resetFilters}
                disabled={loading}
              >
                <i className="bi bi-arrow-counterclockwise me-1"></i>Reset
              </button>
              <button
                className="btn btn-success flex-grow-1"
                onClick={handleExport}
                disabled={exporting}
              >
                {exporting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    Exporting...
                  </>
                ) : (
                  <>
                    <i className="bi bi-download me-2"></i>Export
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm border-0 rounded-4 overflow-hidden">
            <div className="card-body p-0">
              {/* Desktop Table View */}
              <div className="d-none d-md-block table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="bg-light text-dark small text-uppercase fw-bold">
                    <tr>
                      {columns.map(col => (
                        <th key={col.key} className="px-4 py-3 border-0">{col.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="border-top-0">
                    {loading ? (
                      <tr>
                        <td colSpan={columns.length} className="text-center py-5">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </td>
                      </tr>
                    ) : students.length > 0 ? (
                      students.map((student) => {
                        const fullName = `${student.first_name || ''} ${student.middle_name || ''} ${student.last_name || ''}`.trim();
                        return (
                          <tr key={student.id}>
                            {/* <td className="px-4 py-3">#{student.student_id}</td> */}
                            <td className="px-4 py-3">
                              <div className="d-flex align-items-center">
                                <div className="avatar-sm bg-light rounded-circle d-flex align-items-center justify-content-center text-primary fw-bold me-3">
                                  {student.photo ? (
                                    <img src={student.photo} alt={fullName} className="w-100 h-100 object-fit-cover rounded-circle" />
                                  ) : (
                                    student.first_name.charAt(0).toUpperCase() + student.last_name.charAt(0).toUpperCase()
                                  )}
                                </div>
                                <div>
                                  <div className="fw-bold">{fullName} <span className="text-muted small">({student.batch_name})</span></div>
                                  <div className="text-muted small">{student.email_id}</div>
                                </div>
                              </div>
                            </td>
                            {/* <td className="px-4 py-3 text-muted">{student.email_id}</td> */}
                            <td className="px-4 py-3 text-muted">{student.mobile_no}</td>
                            <td className="px-4 py-3">
                              {student.education_level || 'N/A'}
                            </td>
                            <td className="px-4 py-3 text-muted small">
                              {new Date(student.created_at).toLocaleDateString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </td>
                            <td className="px-4 py-3">
                              <button
                                className="btn btn-sm btn-outline-primary px-3"
                                onClick={() => handleViewDetails(student.id)}
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={columns.length} className="text-center py-5 text-muted">
                          No admission forms found matching your filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="d-block d-md-none">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : students.length > 0 ? (
                  <div className="p-3">
                    {students.map((student) => {
                      const fullName = `${student.first_name || ''} ${student.middle_name || ''} ${student.last_name || ''}`.trim();
                      return (
                        <div key={student.id} className="card border-0 shadow-sm rounded-4 mb-3 overflow-hidden">
                          <div className="card-body p-3">
                            <div className="d-flex align-items-center mb-3">
                              <div className="avatar-sm bg-light rounded-circle d-flex align-items-center justify-content-center text-primary fw-bold me-2" style={{ width: '40px', height: '40px' }}>
                                {student.photo ? (
                                  <img src={student.photo} alt={fullName} className="w-100 h-100 object-fit-cover rounded-circle" />
                                ) : (
                                  student.first_name.charAt(0).toUpperCase() + student.last_name.charAt(0).toUpperCase()
                                )}
                              </div>
                              <div className="flex-grow-1 overflow-hidden">
                                <div className="fw-bold text-truncate">{fullName} <span className="text-muted small">({student.batch_name})</span></div>
                                <div className="text-muted small text-truncate">{student.email_id}</div>
                              </div>
                            </div>

                            <div className="row g-2 small">
                              <div className="col-6">
                                <div className="text-muted mb-1">Mobile</div>
                                <div className="fw-medium">{student.mobile_no}</div>
                              </div>
                              <div className="col-6">
                                <div className="text-muted mb-1">Education</div>
                                <div className="fw-medium text-truncate">{student.education_level || 'N/A'}</div>
                              </div>
                              <div className="col-12 mt-2 pt-2 border-top">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div className="text-muted italic">
                                    {new Date(student.created_at).toLocaleDateString('en-IN', {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric'
                                    })}
                                  </div>
                                  <button
                                    className="btn btn-primary btn-sm px-3"
                                    onClick={() => handleViewDetails(student.id)}
                                  >
                                    View Details
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-5 text-muted">
                    No admission forms found matching your filters.
                  </div>
                )}
              </div>
            </div>
            {!loading && students.length > 0 && (
              <div className="card-footer bg-white border-0 py-3">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <div className="text-muted small">
                    Showing {students.length} of {pagination.total} students
                  </div>
                  <PaginationControls
                    currentPage={pagination.currentPage}
                    lastPage={pagination.lastPage}
                    onPageChange={handlePageChange}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .avatar-sm {
          font-size: 0.8rem;
        }
        .table thead th {
          font-size: 0.75rem;
          letter-spacing: 0.05em;
        }
        .card {
          transition: transform 0.2s;
        }
      `}</style>
    </div>
  );
};

export default IFStudentAdmissionList;
